import { useNavigate } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Zap, Shield, Smartphone, Gauge, Search, TrendingUp, Check, Sparkles } from "lucide-react";
import ScoreGauge from "@/components/ScoreGauge";

const categories = [
  { icon: TrendingUp, name: "Conversion", desc: "CTA, tunnel, urgence, upsell" },
  { icon: Search, name: "UX", desc: "Navigation, parcours, clarté" },
  { icon: Shield, name: "Confiance", desc: "Avis, garanties, réassurance" },
  { icon: Smartphone, name: "Mobile", desc: "Responsive, tap targets, sticky" },
  { icon: Gauge, name: "Performance", desc: "Vitesse, images, LCP" },
  { icon: Zap, name: "Fiches produit", desc: "Photos, descriptifs, variantes" },
];

const scoreExample = [
  { cat: "Conversion", val: 68 },
  { cat: "Confiance", val: 82 },
  { cat: "UX", val: 76 },
  { cat: "Mobile", val: 71 },
  { cat: "Performance", val: 69 },
];

export default function Landing() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    navigate(`/analyze?url=${encodeURIComponent(url.trim())}`);
  };

  return (
    <div className="min-h-screen bg-[#080A11] text-slate-100 overflow-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-40 pb-32 px-6">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute top-20 -left-40 w-[500px] h-[500px] glow-violet" />
        <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] glow-cyan" />

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 mb-8">
            <Sparkles className="w-3.5 h-3.5 text-violet-400" />
            <span className="text-xs font-semibold uppercase tracking-widest text-violet-300">
              Assistant CRO IA · Shopify & E-commerce
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] text-white">
            Votre boutique reçoit des visiteurs.
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-blue-400 bg-clip-text text-transparent">
              Mais pourquoi ils n'achètent pas ?
            </span>
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
            RevenueFix AI analyse votre boutique et identifie les problèmes qui freinent
            vos conversions. Score, priorités, corrections — en 30 secondes.
          </p>

          <form onSubmit={submit} className="mt-12 max-w-xl mx-auto flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="hero-url-input"
              placeholder="votreboutique.myshopify.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="h-14 bg-[#0F1322]/80 border-slate-700/80 text-white placeholder:text-slate-500 focus-visible:ring-violet-500 text-base rounded-xl px-5"
            />
            <Button
              data-testid="hero-audit-button"
              type="submit"
              className="h-14 px-7 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white font-semibold rounded-xl shadow-xl shadow-violet-600/25 border-0 whitespace-nowrap"
            >
              Analyser gratuitement <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
          <p className="mt-4 text-xs text-slate-500">
            Gratuit · Aucune installation · Résultats en 30 secondes
          </p>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="relative px-6 py-24 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="text-xs font-semibold uppercase tracking-widest text-violet-400 mb-4">
            8 axes d'analyse
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Ce que RevenueFix AI analyse
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
            Une vue à 360° de votre boutique, sur les leviers qui font vraiment la différence.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((c) => (
            <div
              key={c.name}
              data-testid={`category-${c.name.toLowerCase()}`}
              className="glass rounded-2xl p-6 hover:bg-[#161C33]/60 hover:border-violet-500/30 transition-all group"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500/20 to-blue-500/20 border border-violet-500/20 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <c.icon className="w-5 h-5 text-violet-300" />
              </div>
              <div className="font-display text-lg font-semibold text-white">{c.name}</div>
              <p className="text-sm text-slate-400 mt-1">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative px-6 py-24 max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="text-xs font-semibold uppercase tracking-widest text-violet-400 mb-4">Comment ça marche</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white">4 étapes, zéro friction</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { n: "01", t: "Entrez votre URL", d: "Shopify, WooCommerce ou toute boutique en ligne." },
            { n: "02", t: "RevenueFix analyse", d: "IA CRO + heuristiques sur 8 axes clés." },
            { n: "03", t: "Recevez votre score", d: "Score global + score par catégorie." },
            { n: "04", t: "Appliquez les fixes", d: "Actions concrètes et priorisées." },
          ].map((s) => (
            <div key={s.n} className="relative glass rounded-2xl p-6">
              <div className="font-mono-alt text-xs text-violet-400 mb-3">{s.n}</div>
              <div className="font-display text-lg font-semibold text-white">{s.t}</div>
              <p className="text-sm text-slate-400 mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SCORE EXAMPLE */}
      <section className="relative px-6 py-24 max-w-6xl mx-auto">
        <div className="glass rounded-3xl p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] glow-violet opacity-40" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-violet-400 mb-4">Exemple de rapport</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Un score clair.
                <br /> Des priorités actionnables.
              </h2>
              <p className="mt-4 text-slate-400 max-w-md">
                Chaque audit inclut un Revenue Score global, un breakdown par catégorie,
                et une liste de problèmes triés par priorité (critique, important, faible).
              </p>
              <div className="mt-8 space-y-3">
                {scoreExample.map((s) => (
                  <div key={s.cat}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-slate-300">{s.cat}</span>
                      <span className="font-mono-alt text-slate-100 font-semibold">{s.val}/100</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-violet-500 to-blue-500 rounded-full transition-all"
                        style={{ width: `${s.val}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center">
              <ScoreGauge score={73} size={280} label="Revenue Score" />
            </div>
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="relative px-6 py-24 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="text-xs font-semibold uppercase tracking-widest text-violet-400 mb-4">Pricing</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Simple, transparent, sans engagement
          </h2>
        </div>
        <div className="text-center">
          <Button
            data-testid="landing-see-pricing-btn"
            onClick={() => navigate("/pricing")}
            className="bg-white/5 hover:bg-white/10 text-white border border-white/10 h-12 px-8"
          >
            Voir les plans <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-slate-800/80 py-12 px-6 mt-16">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            <span className="font-display font-bold text-white">RevenueFix.ai</span>
          </div>
          <p className="text-xs text-slate-500">© 2026 RevenueFix AI — Fait avec passion pour les e-commerçants.</p>
        </div>
      </footer>
    </div>
  );
}
